
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';

interface ExecutionDetails {
  executionId?: string;
  configId: number;
  configName: string;
  startTime: string;
  endTime?: string;
  duration?: number;
  status: string;
  totalPurchaseOrders: number;
  totalItems: number;
  totalValue: number;
  metrics?: any;
  configSnapshot?: any;
  generatedPoIds?: number[];
  errors?: any[];
}

interface PurchaseOrder {
  id: number;
  poNumber: string;
  supplierId: number;
  supplierName: string;
  status: string;
  itemCount: number;
  totalValue: number;
  currencyCode?: string | null;
  createdAt: string;
}

interface SupplierBreakdown {
  id: number;
  name: string;
  poCount: number;
  itemCount: number;
  totalValue: number;
  currencyCode?: string | null;
}

@Component({
  name: 'ExecutionDetailModal'
})
export default class ExecutionDetailModal extends Vue {
  @Prop({ type: Boolean, default: false })
  private value!: boolean;

  @Prop({ type: Number, required: true })
  private executionId!: number;

  private loading = false;
  private error: string | null = null;
  private activeTab = 0;
  private exporting = false;

  private executionData: ExecutionDetails | null = null;
  private generatedPOs: PurchaseOrder[] = [];
  private supplierBreakdown: SupplierBreakdown[] = [];

  private poHeaders = [
    { text: 'PO Number', value: 'poNumber' },
    { text: 'Supplier', value: 'supplierName' },
    { text: 'Status', value: 'status' },
    { text: 'Items', value: 'itemCount', align: 'end' },
    { text: 'Total Value', value: 'totalValue', align: 'end' },
    { text: 'Created', value: 'createdAt' }
  ];

  get isOpen(): boolean {
    return this.value;
  }

  set isOpen(val: boolean) {
    this.$emit('input', val);
  }

  get hasErrors(): boolean {
    return !!(this.executionData?.status === 'failed' || 
           (this.executionData?.errors && this.executionData.errors.length > 0));
  }

  get executionErrors(): any[] {
    return this.executionData?.errors || [];
  }

  get configSnapshot(): any {
    return this.executionData?.configSnapshot || {};
  }

  @Watch('value')
  async onOpenChange(isOpen: boolean): Promise<void> {
    if (isOpen && this.executionId) {
      await this.fetchExecutionDetails();
    }
  }

  private async fetchExecutionDetails(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const { poAutomationAnalyticsService } = await import('@/services/po-automation-analytics.service');
      
      // Fetch execution details
      const response = await poAutomationAnalyticsService.getExecutionDetails(this.executionId);
      this.executionData = response;

      // If we have PO IDs, fetch the actual PO details
      if (response.generatedPoIds && response.generatedPoIds.length > 0) {
        await this.fetchPurchaseOrders(response.generatedPoIds);
      }

      // Calculate supplier breakdown
      this.calculateSupplierBreakdown();

    } catch (err: any) {
      console.error('Failed to fetch execution details:', err);
      this.error = err.message || 'Failed to load execution details';
    } finally {
      this.loading = false;
    }
  }

  private async fetchPurchaseOrders(poIds: number[]): Promise<void> {
    try {
      // Import the purchase order model
      const { PurchaseOrderModel } = await import('@/models/purchase_order');
      
      // Fetch purchase orders by IDs
      const poPromises = poIds.map(id => PurchaseOrderModel.getById(id));
      const purchaseOrders = await Promise.all(poPromises);
      
      // Map the purchase orders to the expected format
      this.generatedPOs = purchaseOrders.map((po) => ({
        id: po.id || 0,
        poNumber: po.poNumber || `PO-${po.id}`,
        supplierId: po.supplierId || 0,
        supplierName: po.supplierName || 'Unknown Supplier',
        status: po.status || 'draft',
        itemCount: po.items?.length || 0,
        totalValue: po.totalAmount || 0,
        currencyCode: po.currencyCode || (po as any).currency_code || null,
        createdAt: po.createdAt || new Date().toISOString()
      }));
    } catch (err) {
      console.error('Failed to fetch purchase orders:', err);
      // If fetching fails, show empty list rather than mock data
      this.generatedPOs = [];
    }
  }

  private calculateSupplierBreakdown(): void {
    const breakdown: { [key: string]: SupplierBreakdown } = {};

    this.generatedPOs.forEach(po => {
      if (!breakdown[po.supplierName]) {
        breakdown[po.supplierName] = {
          id: po.supplierId,
          name: po.supplierName,
          poCount: 0,
          itemCount: 0,
          totalValue: 0,
          currencyCode: po.currencyCode || null
        };
      }

      breakdown[po.supplierName].poCount++;
      breakdown[po.supplierName].itemCount += po.itemCount;
      breakdown[po.supplierName].totalValue += po.totalValue;
    });

    this.supplierBreakdown = Object.values(breakdown).sort((a, b) => b.totalValue - a.totalValue);
  }

  private formatDateTime(dateString?: string): string {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  }

  private formatDuration(seconds?: number): string {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  private formatCurrency(value?: number, currencyCode?: string | null): string {
    if (value === undefined || value === null) return '-';
    if (currencyCode) {
      return CurrencyHelper.formatCurrencyWithCode(value, currencyCode);
    }
    return CurrencyHelper.formatCurrency(value);
  }

  private getStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'running':
      case 'processing':
        return 'info';
      default:
        return 'grey';
    }
  }

  private getPOStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'draft':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'grey';
    }
  }

  private navigateToConfig(): void {
    if (this.executionData?.configId) {
      this.$router.push({
        name: 'po-automation-config-detail',
        params: { id: String(this.executionData.configId) }
      });
      this.close();
    }
  }

  private navigateToPO(po: PurchaseOrder): void {
    this.$router.push({
      name: 'purchase-order-detail',
      params: { id: String(po.id) }
    });
    this.close();
  }

  private async exportPOs(): Promise<void> {
    this.exporting = true;
    try {
      // Create CSV content
      const headers = ['PO Number', 'Supplier', 'Status', 'Items', 'Total Value', 'Created'];
      const rows = this.generatedPOs.map(po => [
        po.poNumber,
        po.supplierName,
        po.status,
        po.itemCount,
        po.totalValue.toFixed(2),
        new Date(po.createdAt).toLocaleDateString()
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `execution-${this.executionId}-pos.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      this.$emit('export-complete');
    } catch (err) {
      console.error('Failed to export POs:', err);
      this.$emit('export-error', err);
    } finally {
      this.exporting = false;
    }
  }

  private retry(): void {
    this.fetchExecutionDetails();
  }

  private close(): void {
    this.isOpen = false;
    this.$emit('close');
  }

  // Lifecycle hooks
  async mounted(): Promise<void> {
    // If the modal is already open when mounted, fetch the data
    if (this.value && this.executionId) {
      await this.fetchExecutionDetails();
    }
  }
}
